import { selectLoginStateWaiting, selectUser } from 'gossamer-front-end';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import styles from './Sidebar.module.css';
import { CustomInfo } from 'cb-unified';

interface MenuItemProps {
  name: string;
  path: string;
  svg: string;
  roles: string[];
  plans?: string[];
  additionalText?: string;
}

interface LockedMenuItemProps {
  name: string;
  svg: string;
  additionalText: string;
}

// Variant: shows the item as unavailable on the current plan.
// Clicking it sends the user to the upgrade plan page.
const LockedMenuItem = (props: LockedMenuItemProps) => {
  const upgradePath = '/website/upgrade-plan';
  const unavailableText = 'Not available on your plan';
  return (
    <li>
      <a
        href={upgradePath}
        className={[styles.navItem, styles.navItemLocked].join(' ')}
        title={`${unavailableText}. ${props.additionalText} Click to upgrade.`}
      >
        <div className={styles.navItemBg}></div>
        <div className={styles.navItemHighlight}></div>
        <div className={styles.navItemIcon}>
          <img src={props.svg} />
        </div>
        <div className={styles.navItemText}>
          <div className={styles.navItemLabelRow}>
            <span>{props.name}</span>
            <span className={styles.navItemBadge}>Upgrade</span>
          </div>
          <div className={styles.navItemSubtext}>{unavailableText}</div>
        </div>
      </a>
    </li>
  );
};

const ALL_PLANS = ['personal', 'paid', 'society', 'pro', 'premium', 'marketing', 'enterprise'];
const ALL_PLANS_EXCEPT_PERSONAL = ALL_PLANS.filter((plan) => plan !== 'personal');
const PREMIUM_AND_ENTERPRISE = ['premium', 'enterprise'];
const ENTERPRISE_ONLY = ['enterprise'];

interface SidebarProps {
  onClose: () => void;
}

export const Sidebar = (props: SidebarProps): React.JSX.Element => {
  const isWaiting = useSelector(selectLoginStateWaiting);
  const user = useSelector(selectUser);
  const userRoleId = user?.roleId;
  const userPlanId = (user?.customInfo as CustomInfo)?.adminInstitutionType || 'personal';
  const userInstitutionId = user?.orgPath;
  const onClose = props.onClose;

  const MenuItem = (props: MenuItemProps) => {
    if (!props.roles.includes(userRoleId)) {
      return null;
    }
    if (userRoleId !== 'staff' && (!props.plans || !props.plans.includes(userPlanId))) {
      const additionalText =
        props.additionalText || 'Upgrade your plan to unlock this feature and get access to more tools.';
      return <LockedMenuItem name={props.name} svg={props.svg} additionalText={additionalText} />;
    }
    return (
      <li>
        <NavLink to={props.path} className={styles.navItem} onClick={onClose}>
          <div className={styles.navItemBg}></div>
          <div className={styles.navItemHighlight}></div>
          <div className={styles.navItemIcon}>
            <img src={props.svg} />
          </div>
          <div>{props.name}</div>
        </NavLink>
      </li>
    );
  };

  return (
    <aside className={styles.sidebar}>
      {!!user?.userId && (
        <nav className={styles.nav}>
          <ul className={styles.navGroup}>
            <MenuItem
              name="Dashboard"
              path={`/admin/institution/display/${userInstitutionId}`}
              svg="/assets/icons/home.svg"
              roles={['admin', 'staff']}
              plans={ALL_PLANS_EXCEPT_PERSONAL}
            />
            <MenuItem
              name="Reporting"
              path="/admin/reporting/inst-week-search"
              svg="/assets/icons/chart.svg"
              roles={['user', 'admin', 'staff']}
              plans={PREMIUM_AND_ENTERPRISE}
              additionalText="Access reporting to understand engagement and performance over time."
            />
            <MenuItem
              name="Payment Summary"
              path="/admin/payments/user-summary"
              svg="/assets/icons/bolt.svg"
              roles={['user']}
              plans={ALL_PLANS}
            />
            <MenuItem
              name="Payment Summary"
              path="/admin/payments/institution-summary"
              svg="/assets/icons/bolt.svg"
              roles={['admin', 'staff']}
              plans={ALL_PLANS_EXCEPT_PERSONAL}
            />
          </ul>

          <ul className={styles.navGroup}>
            <MenuItem
              name="Search for Events"
              path="/admin/event/search"
              svg="/assets/icons/ticket.svg"
              roles={['admin', 'staff']}
              plans={ALL_PLANS}
            />
            <MenuItem
              name="Event Calendar"
              path={`/admin/event/calendar`}
              svg="/assets/icons/calendar.svg"
              roles={['admin', 'staff']}
              plans={ALL_PLANS}
            />
            <MenuItem
              name={'Followers, Members & Admins'}
              path={`/admin/institution-member/search`}
              svg="/assets/icons/user.svg"
              roles={['user', 'admin', 'staff']}
              plans={ALL_PLANS_EXCEPT_PERSONAL}
              additionalText="Manage your institution's followers, members and admins in one place."
            />
            <MenuItem
              name="Groups"
              path={`/admin/institution-group/search`}
              svg="/assets/icons/squares2x2.svg"
              roles={['user', 'admin', 'staff']}
              plans={ENTERPRISE_ONLY}
              additionalText="Organise members into groups for easier management and reporting."
            />
          </ul>

          <ul className={styles.navGroup}>
            <MenuItem
              name="Absence Periods"
              path="/admin/absence-period/search"
              svg="/assets/icons/chart-pie.svg"
              roles={['user', 'admin', 'staff']}
              plans={ENTERPRISE_ONLY}
              additionalText="Track absence periods to support better planning and visibility."
            />
            <MenuItem
              name="Event Profiles"
              path="/admin/event-profile/search"
              svg="/assets/icons/adjustments-horizontal.svg"
              roles={['user', 'admin', 'staff']}
              plans={ALL_PLANS_EXCEPT_PERSONAL}
            />
            <MenuItem
              name="Feedback Forms"
              path="/admin/feedback-form/search"
              svg="/assets/icons/chat-bubble-left-right.svg"
              roles={['user', 'admin', 'staff']}
              plans={ALL_PLANS_EXCEPT_PERSONAL}
            />
            {/* <MenuItem
      name="Timetables"
      path="/admin/timetable/search"
      svg="/assets/icons/calendar.svg"
      roles={['staff']}
      plans={ENTERPRISE_ONLY}
      additionalText="Create and manage timetables across your institution."
    /> */}
            <MenuItem
              name="Tracking Locations"
              path="/admin/location/search"
              svg="/assets/icons/map-pin.svg"
              roles={['admin', 'staff']}
              plans={ENTERPRISE_ONLY}
              additionalText="Track locations to improve attendance and safeguarding workflows."
            />
            <MenuItem
              name="Transactions"
              path="/admin/transaction/search"
              svg="/assets/icons/bolt.svg"
              roles={['admin', 'staff']}
              plans={ENTERPRISE_ONLY}
              additionalText="See a full audit trail of saves and system actions."
            />
            <MenuItem
              name="Workflows"
              path="/admin/workflow/manage"
              svg="/assets/icons/map-pin.svg"
              roles={['admin', 'staff']}
              plans={ENTERPRISE_ONLY}
              additionalText="Manage and track workflows across your institution."
            />
          </ul>

          {user.roleId === 'staff' && (
            <ul className={styles.navGroup}>
              <div className={styles.navItem}>Staff Only</div>
              <MenuItem name="Users" path="/admin/user/search" svg="/assets/icons/user.svg" roles={['staff']} />
              <MenuItem
                name="Institutions"
                path="/admin/institution/search"
                svg="/assets/icons/building-library.svg"
                roles={['staff']}
              />
              <MenuItem
                name="Org Units"
                path="/admin/org-unit/search"
                svg="/assets/icons/squares2x2.svg"
                roles={[/*'user', 'admin',*/ 'staff']}
                plans={ENTERPRISE_ONLY}
                additionalText="Create and manage your organisation hierarchy."
              />
              <MenuItem
                name="Outbound Email"
                path="/admin/outbound-email/search"
                svg="/assets/icons/envelope.svg"
                roles={['staff']}
                plans={ENTERPRISE_ONLY}
                additionalText="Send outbound emails to your users at scale."
              />
              <MenuItem
                name="Payments In"
                path="/admin/payments-in/search"
                svg="/assets/icons/bolt.svg"
                roles={['staff']}
              />
              <MenuItem
                name="Payments Out"
                path="/admin/payments-out/search"
                svg="/assets/icons/bolt.svg"
                roles={['staff']}
              />
              <MenuItem
                name="PN Diagnostics"
                path="/admin/push-notification/diagnostics"
                svg="/assets/icons/bell-alert.svg"
                roles={['staff']}
              />
            </ul>
          )}
        </nav>
      )}
      {!user?.userId && isWaiting && <div>Loading...</div>}
      {!user?.userId && !isWaiting && <NavLink to="/auth/login">Log-in</NavLink>}
    </aside>
  );
};
